import { useState, useCallback, useEffect, useRef } from 'react';
import * as api from '../api/client';

const POLL_INTERVAL_MS = 3000;

export function useDocuments(activeId) {
  const [documents, setDocuments] = useState([]);
  const pollTimerRef = useRef(null);

  // ── Polling ───────────────────────────────────────────────────────────────

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const pollOnce = useCallback(async (sid) => {
    try {
      const data = await api.getDocuments(sid);
      setDocuments(data);
      // Stop polling once every document has left the 'pending' state
      const stillPending = data.some(d => d.status === 'pending');
      if (!stillPending) stopPolling();
    } catch {
      // Silently ignore poll errors — don't disrupt the UI
    }
  }, [stopPolling]);

  const startPolling = useCallback((sid) => {
    stopPolling();
    pollTimerRef.current = setInterval(() => pollOnce(sid), POLL_INTERVAL_MS);
  }, [stopPolling, pollOnce]);

  // Clean up the interval when the active topic changes or the component unmounts
  useEffect(() => {
    return () => stopPolling();
  }, [activeId, stopPolling]);

  // ── Public API ────────────────────────────────────────────────────────────

  const load = useCallback(async (sid) => {
    if (!sid) { setDocuments([]); return; }
    try {
      const data = await api.getDocuments(sid);
      setDocuments(data);
      if (data.some(d => d.status === 'pending')) startPolling(sid);
    } catch {
      setDocuments([]);
    }
  }, [startPolling]);

  const clear = useCallback(() => {
    stopPolling();
    setDocuments([]);
  }, [stopPolling]);

  /**
   * Upload a single file. Replaces any existing doc with the same filename
   * in local state (mirrors the backend upsert behaviour).
   */
  const upload = useCallback(async (file, onUploadProgress) => {
    if (!activeId) return;
    const result = await api.uploadDocument(activeId, file, onUploadProgress);
    const newDoc = {
      id:          result.doc_id,
      filename:    result.filename,
      uploaded_at: new Date().toISOString(),
      status:      'pending',
    };
    setDocuments(prev => {
      const filtered = prev.filter(d => d.filename !== result.filename);
      return [newDoc, ...filtered];
    });
    // A new pending doc means we should be polling
    startPolling(activeId);
    return result;
  }, [activeId, startPolling]);

  /**
   * Upload multiple files sequentially, reporting per-file progress.
   */
  const uploadMany = useCallback(async (files, onProgress) => {
    const errors = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        await upload(file, (evt) => {
          onProgress?.({
            filename:  file.name,
            loaded:    evt.loaded,
            total:     evt.total,
            fileIndex: i,
            fileCount: files.length,
          });
        });
      } catch (err) {
        errors.push({ filename: file.name, error: err });
      }
    }
    return errors;
  }, [upload]);

  const ingestUrl = useCallback(async (url) => {
    if (!activeId) return;
    const result = await api.ingestUrl(activeId, url);
    const newDoc = {
      id:          result.doc_id,
      filename:    result.url,
      uploaded_at: new Date().toISOString(),
      status:      'pending',
    };
    setDocuments(prev => {
      const filtered = prev.filter(d => d.filename !== result.url);
      return [newDoc, ...filtered];
    });
    startPolling(activeId);
    return result;
  }, [activeId, startPolling]);

  const remove = useCallback(async (docId) => {
    if (!activeId) return;
    await api.deleteDocument(activeId, docId);
    setDocuments(prev => {
      const next = prev.filter(d => d.id !== docId);
      // If no more pending docs after removal, stop polling
      if (!next.some(d => d.status === 'pending')) stopPolling();
      return next;
    });
  }, [activeId, stopPolling]);

  const reassess = useCallback(async (docId) => {
    if (!activeId) return;
    const result = await api.reassessDocument(activeId, docId);
    // Update the document in local state immediately with the new
    // verdict and report — no need to wait for the next poll.
    setDocuments(prev => prev.map(d =>
      d.id === docId
        ? { ...d, quality_verdict: result.verdict, ingest_report: result.report }
        : d
    ));
    return result;
  }, [activeId]);


  return { documents, load, clear, upload, uploadMany, ingestUrl, remove, reassess };  
}

